import { Box, Button, Flex } from '@radix-ui/themes';
import { useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute, Link, useLoaderData } from '@tanstack/react-router';
import { useState } from 'react';
import { TbHome } from 'react-icons/tb';
import { z } from 'zod';
import { Header } from '~/components/Header';
import { Logo } from '~/components/Logo';
import { ProjectNavigationToast } from '~/components/PageNavigationToast';
import SharedStyles from '~/components/shared.module.scss';
import Styles from '~/styles/routes.module.scss';
import { AccountPage } from '~/features/account/AccountPage';
import { AccountDropdown } from '~/features/project/components/AccountDropdown';
import { defaultQueryClient } from '~/lib/tanstackQuery';
import { organisationsApi } from '~/services/organisations-api/organisationsApi';

const searchparamsSchema = z.object({
  backToProjectId: z.string().optional(),
  content: z.enum(['billing', 'keys', 'labs', 'privacy', 'channels']).optional(),
});

export const Route = createFileRoute('/_auth/account')({
  component: RouteComponent,
  validateSearch: searchparamsSchema,
  loaderDeps: ({ search }) => ({ search }),
  loader: async ({ context, deps }) => {
    if (!context.organisation) {
      return {
        organisation: undefined,
        invoices: [],
      };
    }

    const invoices = await defaultQueryClient.ensureQueryData(
      organisationsApi.queryOptions(
        'get',
        '/organisations/{organisation_id}/billing/invoices',
        {
          params: {
            path: { organisation_id: context.organisation.id },
          },
        },
        {
          enabled: Boolean(context.organisation.id),
        }
      )
    );

    // for now
    const upcoming = invoices[0];

    const fromStr = new Date(new Date().setMonth(new Date().getMonth() - 6)).toISOString().split('T')[0];

    const lastInvoices = await defaultQueryClient.ensureQueryData(
      organisationsApi.queryOptions(
        'get',
        '/organisations/{organisation_id}/billing/invoices',
        {
          params: {
            path: { organisation_id: context.organisation.id },
            query: {
              from: fromStr,
            },
          },
        },
        {
          enabled: Boolean(context.organisation.id),
        }
      )
    );

    return {
      session: context.session,
      organisations: context.organisations,
      organisation: context.organisation,
      invoices: lastInvoices,
      upcomingInvoice: upcoming,
      backToProjectId: deps.search?.backToProjectId,
      fromStr,
    };
  },
});

function RouteComponent() {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const { organisation, backToProjectId, fromStr } = useLoaderData({ from: '/_auth/account' });

  const { data: invoices } = useSuspenseQuery(
    organisationsApi.queryOptions(
      'get',
      '/organisations/{organisation_id}/billing/invoices',
      {
        params: {
          path: { organisation_id: organisation?.id || '' },
        },
      },
      {
        enabled: Boolean(organisation?.id),
      }
    )
  );

  const { data: lastInvoices } = useSuspenseQuery(
    organisationsApi.queryOptions(
      'get',
      '/organisations/{organisation_id}/billing/invoices',
      {
        params: {
          path: { organisation_id: organisation?.id || '' },
          query: {
            from: fromStr,
          },
        },
      },
      {
        enabled: Boolean(organisation?.id),
      }
    )
  );

  return (
    <Flex direction="column" pb="5" minHeight="0" flexGrow="1">
      <Flex asChild width="100%" justify="between" align="center" gap="2" height="5.5rem">
        {/* <header>
          <Flex align="center" justify="between" width="100%">
            <Flex gap="5" align="center">
              <Logo component="img" appearance="dark" href="/home" />
              {backToProjectId && (
                <Flex
                  align="center"
                  gap="5"
                  display={{
                    initial: 'none',
                    sm: 'flex',
                  }}
                >
                  <div className={SharedStyles.VerticalSeparator} />
                  <Box ml="-2">
                    <ProjectNavigationToast />
                  </Box>
                </Flex>
              )}
            </Flex>
            <Flex gap="3">
              <Link to="/home">
                <Button size="2" variant="surface">
                  <TbHome color="var(--cyan-11)" />
                  Home
                </Button>
              </Link>

              <AccountDropdown isPopoverOpen={isPopoverOpen} setIsPopoverOpen={setIsPopoverOpen} />
            </Flex>
          </Flex>
        </header> */}
        <Header disableLink className={Styles.LayoutHeader}>
          <Flex align="center" justify="between" width="100%">
            <Flex gap="5" align="center">
              {/* <Logo component="img" appearance="dark" href="/home" /> */}
              {backToProjectId && (
                <Flex
                  align="center"
                  gap="5"
                  display={{
                    initial: 'none',
                    sm: 'flex',
                  }}
                >
                  <div className={SharedStyles.VerticalSeparator} />
                  <Box ml="-2">
                    <ProjectNavigationToast />
                  </Box>
                </Flex>
              )}
            </Flex>
            <Flex gap="3">
              <Link to="/home">
                <Button size="2" variant="surface">
                  <TbHome color="var(--cyan-11)" />
                  Home
                </Button>
              </Link>

              <AccountDropdown isPopoverOpen={isPopoverOpen} setIsPopoverOpen={setIsPopoverOpen} />
            </Flex>
          </Flex>
        </Header>
      </Flex>

      {backToProjectId && (
        <Flex display={{ initial: 'flex', sm: 'none' }} mb="4">
          <ProjectNavigationToast />
        </Flex>
      )}
      <Flex align="start" justify="center" flexGrow="1" height="100%" mt="5.5rem">
        <AccountPage invoices={lastInvoices} upcomingInvoice={invoices[0]} />
      </Flex>
    </Flex>
  );
}
